import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { InvoiceEditor } from "@/components/webadvisor/InvoiceEditor";
import { useParams } from "react-router-dom";
import { Loader2, Printer, Download, CreditCard, CheckCircle2, PenLine, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { money } from "@/lib/landingSim";
import { fileUrl } from "@/lib/products";
import { InvoiceStatus, termsLabel } from "@/components/webadvisor/InvoiceTable";
import { PayOptions } from "@/components/webadvisor/PayOptions";

const SHOP_LOGO = "https://customer-assets.emergentagent.com/job_shop-advisor-demo/artifacts/qy6rjh9d_Dynamic%20Logo%20for%20Automotive%20Channel%20Remix%20the%20Whip.png";
const B = process.env.REACT_APP_BACKEND_URL;

const usePaymentReturn = (token, onDone) => {
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) return;
    (async () => {
      for (let i = 0; i < 10; i++) {
        try { const { data } = await api.get(`/api/webadvisor/public/proposal/${token}/payment/${sid}`); if (data.payment_status === "paid") { toast.success("Payment received — thank you!"); break; } } catch { /* retry */ }
        await new Promise(r => setTimeout(r, 2500));
      }
      window.history.replaceState({}, "", window.location.pathname); onDone();
    })();
  }, [token, onDone]);
};

const Header = ({ seller, inv }) => (
  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-6 pb-6 border-b-4 border-blue-600">
    <div>
      <div className="overflow-hidden" style={{ height: 56, width: 250 }}><img src={SHOP_LOGO} alt="ShopAdvisor" className="w-full h-auto" style={{ marginTop: "-38%" }} data-testid="invoice-shop-logo" /></div>
      <div className="text-xs text-slate-500 mt-2 leading-relaxed" data-testid="invoice-seller">
        <p className="font-bold text-slate-800">{seller.name}</p>
        {[seller.address, seller.phone, seller.email, seller.website].filter(Boolean).map(l => <p key={l}>{l}</p>)}
      </div>
    </div>
    <div className="sm:text-right">
      <p className="text-4xl font-black tracking-tight text-slate-900" data-testid="invoice-doc-kind">{inv.doc_kind === "quote" ? "QUOTE" : "INVOICE"}</p>
      <p className="font-bold text-blue-700 mt-1" data-testid="invoice-number">{inv.invoice_number}</p>
      <p className="text-xs text-slate-500">Issued {inv.issued_at}{inv.doc_kind === "quote" && " · valid 30 days"}</p>
      <div className="mt-2">{inv.doc_kind === "quote" ? <span className="text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-blue-50 text-blue-700" data-testid="quote-status">Awaiting your approval</span> : <InvoiceStatus status={inv.status} />}</div>
    </div>
  </div>
);

const BillTo = ({ b }) => (
  <div className="flex items-start justify-between gap-4 bg-slate-50 border border-slate-200 rounded-lg p-4 mt-6" data-testid="invoice-bill-to">
    <div className="text-sm">
      <p className="text-[10px] uppercase tracking-wider font-bold text-blue-600 mb-1">Bill to</p>
      <p className="font-bold text-slate-900">{b.business}</p>
      {[b.contact, b.email, b.phone, b.website].filter(Boolean).map(l => <p key={l} className="text-slate-600 break-all">{l}</p>)}
    </div>
    {b.logo_url && <img src={fileUrl(b.logo_url)} alt="Customer logo" className="h-14 max-w-[140px] object-contain" data-testid="invoice-customer-logo" />}
  </div>
);

const Lines = ({ inv }) => (
  <div className="mt-6 overflow-x-auto">
    <table className="w-full text-sm" data-testid="invoice-lines">
      <thead><tr className="bg-slate-900 text-white text-xs uppercase tracking-wide"><th className="text-left py-2.5 px-3 rounded-l-md">Description</th><th className="text-right px-3 hidden sm:table-cell">Qty</th><th className="text-right px-3 hidden sm:table-cell">Unit price</th><th className="text-right px-3 rounded-r-md">Amount</th></tr></thead>
      <tbody>{inv.items.map((i, n) => (
        <tr key={n} className="border-b border-slate-100" data-testid={`invoice-line-${n}`}>
          <td className="py-3 px-3"><p className="font-semibold text-slate-900">{i.label}</p>{i.detail && <p className="text-xs text-slate-400">{i.detail}</p>}<p className="text-xs text-slate-500 sm:hidden">{i.qty} × {money(i.unit_price)}</p></td>
          <td className="text-right px-3 hidden sm:table-cell">{i.qty}</td><td className="text-right px-3 whitespace-nowrap hidden sm:table-cell">{money(i.unit_price)}</td><td className="text-right px-3 font-semibold whitespace-nowrap">{money(i.amount)}</td>
        </tr>
      ))}</tbody>
    </table>
    <div className="flex justify-end mt-4">
      <div className="w-full sm:w-80 text-sm space-y-1.5" data-testid="invoice-totals">
        <div className="flex justify-between font-bold text-base"><span>Total</span><span data-testid="invoice-total">{money(inv.total)}</span></div>
        <div className="flex justify-between text-slate-500"><span>{inv.deposit_pct >= 100 ? "Due upfront" : `Deposit (${inv.deposit_pct}%)`}</span><span>{money(inv.deposit_due)}</span></div>
        <div className="flex justify-between text-slate-500"><span>Paid to date</span><span data-testid="invoice-paid">{money(inv.paid)}</span></div>
        <div className="flex justify-between font-black text-lg text-blue-700 bg-blue-50 rounded-md px-3 py-2"><span>Balance due</span><span data-testid="invoice-balance">{money(inv.balance)}</span></div>
      </div>
    </div>
  </div>
);

export default function InvoicePage() {
  const { token } = useParams();
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/api/webadvisor/public/invoice/${token}`).then(r => setD(r.data)).catch(() => setErr(true)), [token]);
  useEffect(() => { load(); }, [load]);
  usePaymentReturn(token, load);

  const pay = async () => {
    const a = d.pay_action;
    if (a.kind === "deposit" && a.needs_signature) { window.location.href = `/p/${token}#respond`; return; }
    setBusy(true);
    try {
      const { data } = a.kind === "balance"
        ? await api.post(`/api/webadvisor/public/proposal/${token}/pay-balance`, { origin_url: window.location.origin, return_to: "invoice" })
        : await api.post(`/api/webadvisor/public/proposal/${token}/respond`, { action: "accept", name: d.bill_to.contact, note: "", origin_url: window.location.origin, return_to: "invoice" });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't start the payment"); setBusy(false); }
  };

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="invoice-missing">This invoice is no longer available.</div>;
  if (!d) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  const { invoice: inv, seller, pay_action: a } = d;
  const btn = "text-sm font-bold px-4 py-2.5 rounded-lg flex items-center gap-2";
  return (
    <div className="min-h-screen bg-slate-100 py-8 px-3 sm:px-6 print:bg-white print:p-0" data-testid="invoice-page">
      <div className="max-w-3xl mx-auto mb-4 flex flex-wrap justify-end gap-2 print:hidden">
        {a && <button onClick={pay} disabled={busy} className={`${btn} bg-green-600 hover:bg-green-500 text-white disabled:opacity-50`} data-testid="invoice-pay-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : a.needs_signature ? <PenLine className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />}{a.needs_signature ? "Sign & pay" : "Pay"} {money(a.amount)}{a.kind === "deposit" && inv.deposit_pct < 100 ? " deposit" : ""}</button>}
        {user?.role === "admin" && !editing && <button onClick={() => setEditing(true)} className={`${btn} bg-blue-600 hover:bg-blue-500 text-white`} data-testid="invoice-edit-btn"><Pencil className="w-4 h-4" />Edit invoice</button>}
        <button onClick={() => window.print()} className={`${btn} bg-white border border-slate-300 hover:bg-slate-50 text-slate-800`} data-testid="invoice-print-btn"><Printer className="w-4 h-4" />Print</button>
        <a href={`${B}/api/webadvisor/public/invoice/${token}/pdf`} className={`${btn} bg-slate-900 hover:bg-slate-700 text-white`} data-testid="invoice-pdf-btn"><Download className="w-4 h-4" />Download PDF</a>
      </div>
      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm border border-slate-200 p-6 sm:p-10 print:shadow-none print:border-0 print:rounded-none">
        <Header seller={seller} inv={inv} />
        <BillTo b={d.bill_to} />
        {editing ? <InvoiceEditor token={token} inv={inv} onSaved={() => { setEditing(false); load(); }} onClose={() => setEditing(false)} /> : <Lines inv={inv} />}
        <PayOptions token={token} inv={inv} zelle={d.zelle} due={a?.amount ?? d.zelle?.amount} />
        <div className="mt-8 grid sm:grid-cols-2 gap-4 text-sm">
          <div><p className="font-bold text-slate-900">Payment terms</p><p className="text-slate-600">{termsLabel(inv.deposit_pct)}.</p></div>
          <div><p className="font-bold text-slate-900">How to pay</p><p className="text-slate-600">Pay securely online by card with the Pay button on this page{d.zelle ? ", or send it with Zelle using the details above (put the invoice number in the memo)" : ""}. Questions? Contact {seller.email || seller.name}.</p></div>
        </div>
        {inv.status === "paid" && <p className="mt-6 text-green-700 font-bold flex items-center gap-2" data-testid="invoice-paid-full"><CheckCircle2 className="w-5 h-5" />Paid in full — thank you!</p>}
        <p className="mt-8 font-bold text-blue-700">Thank you for your business!</p>
        <p className="mt-8 pt-4 border-t border-slate-200 text-center text-[11px] text-slate-400" data-testid="invoice-footer">ShopAdvisor&trade; · {seller.website} · Invoice {inv.invoice_number}</p>
      </div>
    </div>
  );
}
